import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import {
  InfoCircleOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  NotificationOutlined,
} from '@ant-design/icons';
import { Badge, Button, Popover } from 'antd';
import { useKeycloak } from '@react-keycloak/web';
import { getAllWorkflowData } from '../services/dsl_DataService';
import NotificationBellIcon from './icons/NotificationBellIcon';
import './NotificationBell.css';

const NotificationContext = createContext(null);

let addNotificationGlobal;

const renderNotificationIcon = (notification) => {
  const levelOrState = notification.level || notification.state;
  switch (levelOrState) {
    case 'INFO':
      return <InfoCircleOutlined style={{ color: '#418cc0' }} />;
    case 'WARNING':
      return <WarningOutlined style={{ color: '#faad14' }} />;
    case 'SUCCESS':
      return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
    case 'ERROR':
    case 'CRITICAL':
      return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
    default:
      return <NotificationOutlined style={{ color: '#418cc0' }} />;
  }
};

export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const [reconnecting, setReconnecting] = useState(false);
  const [detailsVisible, setDetailsVisible] = useState({});
  const [notificationLocal, setNotificationLocal] = useState();
  const [unreadCount, setUnreadCount] = useState(0);
  const { keycloak } = useKeycloak();

  const addNotificationCdfFormat = useCallback((name, level, description) => {
    const key = `${name}-${level}-${description}`;
    setNotifications((prev) => {
      const index = prev.findIndex((n) => `${n.name}-${n.level}-${n.description}` === key);
      let newNotifications;
      if (index !== -1) {
        newNotifications = [
          ...prev.slice(0, index),
          { name, level, description },
          ...prev.slice(index + 1),
        ];
      } else {
        newNotifications = [...prev, { name, level, description }];
        setUnreadCount((c) => c + 1);
      }
      return newNotifications.length > 10 ? newNotifications.slice(-10) : newNotifications;
    });
  }, []);

  const addNotificationWsFormat = useCallback((workflowName, state, components, details) => {
    const key = `${workflowName}-${state}`;
    setNotifications((prev) => {
      const index = prev.findIndex((n) => `${n.workflowName}-${n.state}` === key);
      let newNotifications;
      if (index !== -1) {
        newNotifications = [
          ...prev.slice(0, index),
          { workflowName, state, components, details, type: 'WS' },
          ...prev.slice(index + 1),
        ];
      } else {
        newNotifications = [...prev, { workflowName, state, components, details, type: 'WS' }];
        setUnreadCount((c) => c + 1);
      }
      return newNotifications.length > 10 ? newNotifications.slice(-10) : newNotifications;
    });
  }, []);

  useEffect(() => {
    addNotificationGlobal = addNotificationCdfFormat;
    return () => {
      addNotificationGlobal = null;
    };
  }, [addNotificationCdfFormat]);

  useEffect(() => {
    let ws;
    let reconnectTimeout;

    const connectWebSocket = () => {
      ws = new WebSocket(
        `${window.__RUNTIME_CONFIG__.REACT_APP_API_ROOT_URI.replace(
          'http',
          'ws',
        )}/Kafka/monitoring?timestamp=${Date.now()}`,
      );

      ws.onopen = () => setReconnecting(false);

      ws.onmessage = async (event) => {
        try {
          if (notificationLocal === event.data) return;
          setNotificationLocal(event.data);

          let incomingData;
          try {
            incomingData = JSON.parse(event.data);
          } catch {
            incomingData = [];
          }

          let userWorkflows = await getAllWorkflowData(keycloak.token);
          if (!Array.isArray(userWorkflows)) userWorkflows = [];

          let workflowsToProcess = [];
          if (Array.isArray(incomingData)) {
            workflowsToProcess = incomingData
              .map((workflowStr) => {
                try {
                  return JSON.parse(workflowStr);
                } catch {
                  return null;
                }
              })
              .filter(Boolean);
          } else if (incomingData && typeof incomingData === 'object') {
            if (incomingData.Workflow) {
              workflowsToProcess.push(incomingData);
            } else {
              workflowsToProcess = Object.values(incomingData)
                .map((workflowStr) => {
                  try {
                    return typeof workflowStr === 'string' ? JSON.parse(workflowStr) : workflowStr;
                  } catch {
                    return null;
                  }
                })
                .filter(Boolean);
            }
          }

          workflowsToProcess.forEach((incomingWorkflowString) => {
            if (!Array.isArray(incomingWorkflowString)) return;
            incomingWorkflowString.forEach((workflowNotification) => {
              try {
                const incomingWorkflow = JSON.parse(workflowNotification);
                const matchingWorkflow = userWorkflows.find(
                  (w) => incomingWorkflow?.Workflow === w?.id,
                );
                if (matchingWorkflow) {
                  addNotificationWsFormat(
                    matchingWorkflow.name,
                    incomingWorkflow.State,
                    incomingWorkflow.Components,
                    incomingWorkflow.Details,
                  );
                }
              } catch (e) {
                console.error('Failed to parse workflow notification:', e);
              }
            });
          });
        } catch (err) {
          console.error('Error processing WebSocket event data:', err);
        }
      };

      ws.onclose = () => {
        if (!reconnecting) {
          setReconnecting(true);
          reconnectTimeout = setTimeout(connectWebSocket, 5000);
        }
      };
    };

    if (keycloak?.token) connectWebSocket();

    return () => {
      if (ws) ws.close();
      clearTimeout(reconnectTimeout);
    };
  }, [keycloak.token, notificationLocal, reconnecting, addNotificationWsFormat]);

  const toggleDetails = (index) => {
    setDetailsVisible((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const markAllRead = () => setUnreadCount(0);

  return (
    <NotificationContext.Provider
      value={{ notifications, unreadCount, detailsVisible, toggleDetails, markAllRead }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

function NotificationPopoverContent() {
  const { notifications, detailsVisible, toggleDetails } = useContext(NotificationContext);

  if (notifications.length === 0) {
    return <div className='notification-popover__empty'>No notifications yet</div>;
  }

  return (
    <div className='notification-popover__list'>
      {notifications.map((notification, index) => (
        <div key={index} className='notification-popover__item'>
          <div className='notification-popover__item-header'>
            {renderNotificationIcon(notification)}
            <p className='notification-popover__item-text'>
              <strong>{notification.name || notification.workflowName}</strong>
              {' — '}
              {notification.level || notification.state}
            </p>
          </div>
          {notification.description && (
            <p className='notification-popover__item-text' style={{ marginTop: 6, opacity: 0.75 }}>
              {notification.description}
            </p>
          )}
          {notification.components && (
            <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 12, opacity: 0.8 }}>
              {Object.entries(notification.components).map(([key, value]) => (
                <li key={key}>
                  <strong>{key}:</strong> {value}
                </li>
              ))}
            </ul>
          )}
          {detailsVisible[index] && notification.details && (
            <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 12, opacity: 0.8 }}>
              {Object.entries(notification.details).map(([key, detailsArray]) => (
                <li key={key}>
                  <strong>{key}:</strong>
                  {detailsArray.map((detail, idx) => (
                    <div key={idx} style={{ marginTop: 4 }}>
                      {detail.Status} — {detail.Description}
                    </div>
                  ))}
                </li>
              ))}
            </ul>
          )}
          {notification.type === 'WS' && (
            <Button type='link' size='small' style={{ padding: '4px 0 0' }} onClick={() => toggleDetails(index)}>
              {detailsVisible[index] ? 'Hide details' : 'More details'}
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}

export function NotificationBell() {
  const { unreadCount, markAllRead } = useContext(NotificationContext);
  const [open, setOpen] = useState(false);

  const handleOpenChange = (nextOpen) => {
    setOpen(nextOpen);
    if (nextOpen) markAllRead();
  };

  const content = (
    <div>
      <span className='notification-popover__title'>Notifications</span>
      <NotificationPopoverContent />
    </div>
  );

  return (
    <Popover
      content={content}
      trigger='click'
      open={open}
      onOpenChange={handleOpenChange}
      placement='bottomRight'
      overlayClassName='notification-popover'
    >
      <Badge count={unreadCount} size='small' offset={[-4, 4]}>
        <button type='button' className='notification-bell-btn' aria-label='Notifications'>
          <NotificationBellIcon size={22} />
        </button>
      </Badge>
    </Popover>
  );
}

export const addAppCustomNotification = (name, level, description) => {
  if (addNotificationGlobal) {
    addNotificationGlobal(name, level, description);
  } else {
    console.warn('Notification manager is not initialized.');
  }
};

/** @deprecated Sidebar panel removed; use NotificationProvider + NotificationBell in header */
const NotificationBox = () => null;

export default NotificationBox;
